import {
  createOfflineOperation,
  type OperationClock,
  type OperationIdSource,
} from "../sync/operation";
import type { OfflineOperation } from "../sync/types";

export type NotificationDevicePlatform = "android" | "ios" | "web";
export type NotificationProvider = "local" | "fcm" | "apns" | "web_push";

export interface NotificationDeviceOperationContext {
  actorId: string;
  farmId: string;
  deviceId: string;
  performedAt: string;
}

export interface NotificationDeviceOperationOptions {
  clock?: OperationClock;
  operationIdSource?: OperationIdSource;
}

function options(value?: NotificationDeviceOperationOptions) {
  return {
    ...(value?.clock ? { clock: value.clock } : {}),
    ...(value?.operationIdSource ? { idSource: value.operationIdSource } : {}),
  };
}

function validateContext(context: NotificationDeviceOperationContext) {
  if (!context.actorId || !context.farmId || !context.deviceId || !context.performedAt) {
    throw new Error("NOTIFICATION_DEVICE_CONTEXT_REQUIRED");
  }
}

export function buildNotificationEndpointRegisterOperation(
  context: NotificationDeviceOperationContext,
  input: {
    endpointId: string;
    platform: NotificationDevicePlatform;
    provider: NotificationProvider;
    pushToken?: string;
  },
  operationOptions?: NotificationDeviceOperationOptions,
): OfflineOperation {
  validateContext(context);

  const pushToken = input.pushToken?.trim();

  if (!input.endpointId) {
    throw new Error("NOTIFICATION_ENDPOINT_ID_REQUIRED");
  }

  if ((input.provider === "local" && pushToken) || (input.provider !== "local" && !pushToken)) {
    throw new Error("NOTIFICATION_TOKEN_INVALID");
  }

  return createOfflineOperation(
    {
      operationType: "notification.endpoint_register",
      schemaVersion: 1,
      actorId: context.actorId,
      farmId: context.farmId,
      deviceId: context.deviceId,
      entityId: input.endpointId,
      performedAt: context.performedAt,
      payload: {
        endpointId: input.endpointId,
        deviceId: context.deviceId,
        platform: input.platform,
        provider: input.provider,
        ...(pushToken ? { pushToken } : {}),
      },
    },
    options(operationOptions),
  );
}

export function buildNotificationEndpointSetActiveOperation(
  context: NotificationDeviceOperationContext,
  input: {
    endpointId: string;
    isActive: boolean;
  },
  operationOptions?: NotificationDeviceOperationOptions,
): OfflineOperation {
  validateContext(context);

  if (!input.endpointId) {
    throw new Error("NOTIFICATION_ENDPOINT_ID_REQUIRED");
  }

  return createOfflineOperation(
    {
      operationType: "notification.endpoint_set_active",
      schemaVersion: 1,
      actorId: context.actorId,
      farmId: context.farmId,
      deviceId: context.deviceId,
      entityId: input.endpointId,
      performedAt: context.performedAt,
      payload: {
        endpointId: input.endpointId,
        isActive: input.isActive,
      },
    },
    options(operationOptions),
  );
}
